import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable, Subject, Subscriber} from "rxjs";
import {PaymentRow} from "../types/paymentrow";
import {CardPayment} from "../types/cardPayment";
import {ConfigurationService} from "./configuration";
import {StorageService} from "./storage.service";
import {Receipt} from "../types/receipt";
import {Printer} from "../types/printer";
import {UtilsService} from "./utils";
import {BestoranteService} from "./bestorante";
import { Logger, LoggingService } from "ionic-logging-service";

@Injectable()
export class CardPaymentAndPrintServicePoplatek implements CardPayment, Printer {

  status = "NOT_CONNECTED";
  status$ = new BehaviorSubject<string>("");
  deviceId = null;
  reqIdCounter = 0;
  networkConnection = {};
  synched = false;
  alert = null;
  payCallbackFn = null;
  refundCallbackFn = null;
  notSentChunks = [];
  ws: WebSocket = null;
  apiKey = "0982b8a1-5cf0-4b4f-8177-5bcd3169d5ca";
  apiKeyTest = "e086554a-9a35-4bbf-9f99-a8a7cd7d1dcc";
  apiKeyProd = "0982b8a1-5cf0-4b4f-8177-5bcd3169d5ca";
  userPass = "kassamagneetti:sdslpktmgrivig5pppi5j2trss6hwtigbp2rppha";
  userPassTest = "kassamag:d67bpvyq2nkixldjbvy56banu4fc5b4mijqxxdv2";
  userPassProd = "kassamagneetti:sdslpktmgrivig5pppi5j2trss6hwtigbp2rppha";
  url = "wss://api.poplatek.com/api/v2/terminal/";
  urlProd = "wss://api.poplatek.com/api/v2/terminal/";
  urlTest = "wss://api.sandbox.poplatek.com/api/v2/terminal/";
  reconnectInProgress = false;
  lastError = null;
  printCallback = null;
  initialReconnect = true;

  oldCheckStatus = null;
  oldDataSender = null;
  private posMessage$ = new Subject<string>();
  private readonly fontSize = 24;
  private readonly lineWidth = 26;

  private logger: Logger;

  constructor(private conf: ConfigurationService,
              private storage: StorageService,
              private utils: UtilsService,
              private besto: BestoranteService,
              private loggingService: LoggingService) {
    this.logger = loggingService.getLogger("BestoGo.CardPaymentService");
    this.deviceId = conf.getPoplatekTerminalNr();
    if (!this.deviceId) {
      this.status = "NOT_CONFIGURED";
      this.status$.next(this.status);
    } else {
      this.status = "NOT_CONNECTED";
      this.status$.next(this.status);
      this.reConnect();
    }
  }

  public print(receipt: Receipt): Observable<string> {
    function printRecursively(outputs: string[], callback: () => void) {
      if (outputs.length > 30) {
        const out1 = outputs.slice(0, 30);
        const out2 = outputs.slice(31);
        this.printOld(out1, () => {
          printRecursively.call(this, out2, callback);
        }, this.fontSize, false);
      } else {
        this.printOld(outputs, callback, this.fontSize, true);
      }
    }

    const pr = new Observable((observer: Subscriber<string>) => {
      const outputs = receipt.printOut ? receipt.printOut.split("\n\r") : this.utils.rowsToReceipt(receipt,
        receipt.rows, this.lineWidth, receipt.date, receipt.number, this.conf.confData, null, this.besto.restaurant,
        receipt.clerkID ? receipt.clerkID : null);
      receipt.printOut = outputs.join("\n\r");
      const callback = () => {
          observer.next(receipt.printOut);
      };
      printRecursively.call(this, outputs, callback());
    });
    return pr;
  }

  public printSlip(activeReceipt: Receipt, copy?: boolean): Observable<string> {
    const pr = new Observable((observer: Subscriber<string>) => {
      this.logger.debug("printSlip", "Printing slip: ", activeReceipt);
      for (const payment of activeReceipt.payments) {
        if (payment.payment === "CARD") {
          const rawData = JSON.parse(payment.rawPtData);
          const callback = () => {
            observer.next(rawData);
          };
          const output = this.utils.cardRawDataToSlip(payment, rawData, this.lineWidth);
          this.printOld(output, callback(), this.fontSize, false);
        }
      }
      return {
        unsubscribe: () => {
        }
      };
    });
    return pr;
  }

  endOfDayReconciliation(day: Date): Observable<any> {
    const pr = new Observable((observer: Subscriber<any>) => {
      console.log("Silently reconciliating in Poplatek PT");
      observer.next(true);
    });
    return pr;
  }

  public pay(receipt: Receipt, paymentLine): Observable<PaymentRow> {
    const pr = new Observable((observer: Subscriber<PaymentRow>) => {
      const params = this.generateParams() as any;
      params.amount = Math.round(paymentLine.amount + paymentLine.vat);
      params.currency = "EUR";
      params.receipt_id = receipt.number;
      params.stop_on_app_info = false;
      this.payCallbackFn = (back, error) => {
        if (!error) {
          this.logger.info("pay", "Payment succeeded: ", back);
          const paymentRow: PaymentRow = {
            payment: "CARD",
            cardTerminalTransactionId: back.cardTerminalTransactionId,
            sum: (back.rawPtData.amount / 100),
            realSum: (back.rawPtData.amount / 100),
            authorization: back.authorization,
            cardTerminalUsageMode: back.cardTerminalUsageMode,
            rawPtData: JSON.stringify(back.rawPtData)
          };
          observer.next(paymentRow);
          observer.complete();
          this.posMessage$.next(null);
        } else {
          this.posMessage$.next(null);
          this.logger.error("pay", "Payment error: ", error);
          observer.error(error);
        }
      };
      //this.events.publish("printLog", "POPLATEK.purchase " + JSON.stringify(params));
      this.posMessage$.next(null);
      this.send("Purchase", params);
      return {
        unsubscribe: () => {
        }
      };
    });
    return pr;
  }

  public refund(receipt, paymentRow: PaymentRow): Observable<PaymentRow> {
    const pr = new Observable((observer: Subscriber<PaymentRow>) => {
      const params = this.generateParams() as any;
      const sum = paymentRow.realSum? paymentRow.realSum : paymentRow.sum;
      this.logger.debug("refund", "Sum: ", sum, paymentRow);
      params.amount = Math.abs(Math.round(sum * 100));
      params.currency = "EUR";
      params.receipt_id = receipt.number;
      if (paymentRow.cardTerminalTransactionId) {
        params.transaction_id = paymentRow.cardTerminalTransactionId;
      }
      this.refundCallbackFn = (back, error) => {
        if (!back) {
          observer.error("Not supported");
          return;
        } else {
          const paymentRowCreated: PaymentRow = {
            payment: "CARD",
            cardTerminalTransactionId: back.cardTerminalTransactionId,
            sum: (back.amount / 100),
            authorization: back.authorization,
            cardTerminalUsageMode: back.cardTerminalUsageMode,
            rawPtData: JSON.stringify(back.rawPtData)
          };
          observer.next(paymentRowCreated);
          observer.complete();
        }
      };
      //this.events.publish("printLog", "POPLATEK.refund " + JSON.stringify(params));
      this.send("Refund", params);
      return {
        unsubscribe: () => {
        }
      };
    });
    return pr;
  }

  public abortPayment(receipt: Receipt): Observable<Receipt> {
    const pr = new Observable((observer: Subscriber<Receipt>) => {
      this.send("Abort", this.generateParams());
      return {
        unsubscribe: () => {
        }
      };
    });
    return pr;
  }

  public getId(): string {
    return "Poplatek WS";
  }

  public init(): void {
    this.reConnect();
  }

  public getStatus(): Observable<string> {
    return this.status$;
  }

  public getPosMessage(): Observable<string> {
    return this.posMessage$;
  }

  private reConnect() {
    const isDev = this.conf.isPoplatekDevEnv();
    if (isDev) {
      this.url = this.urlTest;
      this.apiKey = this.apiKeyTest;
      this.userPass = this.userPassTest;
    } else {
      this.url = this.urlProd;
      this.apiKey = this.apiKeyProd;
      this.userPass = this.userPassProd;
    }
    this.deviceId = this.conf.getPoplatekTerminalNr();
    this.logger.trace("reConnect", "Reconnect PoplatekWs: ", this.deviceId);
    if (!this.deviceId) {
      this.logger.error("reConnect", "No poplatek ID defined");
      return;
    }
    if (this.oldCheckStatus) {
      clearInterval(this.oldCheckStatus);
    }
    this.oldCheckStatus = setInterval(() => {
      this.checkStatusPeriodically();
    }, 1000 * 30);
    if (this.oldDataSender) {
      clearInterval(this.oldDataSender);
    }
    this.oldDataSender = setInterval(() => {
      this.sendChunksSlowly();
    }, 1000);
    this.reconnectInProgress = true;
    setTimeout(() => {
      this.connect().then(() => {
        this.reconnectInProgress = false;
        this.logger.trace("reConnect", "Sending sync to spire");
        setTimeout(() => {
          this.send("_Sync");
        }, 100);
        setTimeout(() => {
          this.send("NetworkStart");
        }, 1000);
      }).catch((err) => {
        this.reconnectInProgress = false;
        if (this.initialReconnect) {
          setTimeout(() => {
            this.checkStatusPeriodically();
          }, 5000);
        }
        this.initialReconnect = false;
      });
    }, 100);
  }

  private sendChunksSlowly() {
    let i = 5;
    while (i > 0 && this.notSentChunks.length > 0) {
      if (this.notSentChunks.length > 0) {
        const chunk = this.notSentChunks.shift();
        const b64encoded = btoa(String.fromCharCode.apply(null, chunk.data));
        this.send("Data", {
          id: chunk.conn_id,
          data: b64encoded
        }, true);
        this.logger.trace("sendChunksSlowly", "Sending data: " + b64encoded.length);
      }
      i--;
    }
  }

  private checkStatusPeriodically() {
    if (this.status === "CONNECTED") {
      this.send("_Sync");
      setTimeout(() => {
        this.send("NetworkStart");
      }, 300);
    } else if (this.status === "DISCONNECTED") {
      setTimeout(() => {
        this.reConnect();
      }, 300);

    } else if (this.status === "READY") {
      this.send("Status", this.generateParams());
    } else if (this.status === "NETWORK_CONNECTED") {
      this.send("Status", this.generateParams());
    }
    this.send("_Keepalive", this.generateParams());
  }

  private connect(): Promise<any> {
    this.synched = false;
    this.logger.info("connect", "Connect called: ", this.userPass);
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url + this.deviceId + "/jsonpos",
          ["jsonrpc2.0", "x-popla-auth-" + btoa(this.userPass)
            .replace(/\//g, "")
            .replace(/\+/g, "-")
            .replace(/=/g, "")]);
        this.status = "DISCONNECTED";
        this.status$.next(this.status);
        this.ws.onopen = (e) => {
          this.status = "CONNECTED";
          this.status$.next(this.status);
          this.logger.debug("connect", "Connected", e);
          setTimeout(() => {
            resolve("OK");
          }, 200);
        };
        this.ws.onclose = (e) => {
          this.status = "DISCONNECTED";
          this.status$.next(this.status);
          this.logger.debug("connect", "Disconnected", e);
          if (!this.reconnectInProgress) {
            setTimeout(() => {
              this.reConnect();
            }, 1000);
          }
          reject();
        };
        this.ws.onmessage = (m) => {
          this.handleGenericResponses(JSON.parse(m.data));
        };
      } catch (e) {
        this.logger.error("connect", "Unable to make ws connection: ", e);
        if (!this.reconnectInProgress) {
          setTimeout(() => {
            this.reConnect();
          }, 1000);
        }
        reject();
      }
    });
  }

  private generateParams() {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      protocol_version: 1,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      api_key: this.apiKey,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      timestamp: new Date().toISOString(),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      pos_id: 1,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      sale_location_code: "BestoGO: " + this.conf.getGoUuid(),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cashier_code: this.conf.getUsername(),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cashier_language: "fi"
    };
  }

  private chunkArray(myArray, chunkSize) {
    let index = 0;
    const arrayLength = myArray.length;
    const tempArray = [];

    for (index = 0; index < arrayLength; index += chunkSize) {
      const myChunk = myArray.slice(index, index + chunkSize);
      tempArray.push(myChunk);
    }

    return tempArray;
  }

  /**
   * Called whenever some data is coming
   *
   * @param data
   */
  private handleGenericResponses(data) {
    this.logger.info("handleGenericResponses", "<---- Received", data);
    if (data.response_to === "_Sync") {
      this.synched = true;
      this.logger.debug("handleGenericResponses", "Synched", this.synched);
      if (this.synched) {
        // this is for initializing font change for canvas, otherwise first receipt will have default font
        this.createCanvas(["@%$&*"], 24);
      }
      this.status = "SYNCHED";
      this.status$.next(this.status);
      setTimeout(() => {
        this.send("Status", this.generateParams());
      }, 100);
    } else if (data.method === "_CloseReason") {
      this.logger.error("handleGenericResponses", "Closed", data);
      this.status = "CLOSED";
      this.status$.next(this.status);
      this.synched = false;
      if (data.params && data.params.error && data.params.error.data) {
        this.lastError = data.params.error.data.details;
      }
      this.ws.close();
    } else if (data.method === "_Keepalive") {
      setTimeout(() => {
        this.sendResponseTo("_Keepalive", data.id);
      }, 300);
    } else if (data.method === "NetworkConnect") {
      this.status = "NETWORK_CONNECTED";
      this.status$.next(this.status);
      this.networkConnection[data.params.connection_id] = data.params;
      this.logger.debug("Network connection", this.networkConnection);
      const win = window as any;
      const socket = new win.Socket();
      socket.onData = (data2) => {
        this.logger.info("handleGenericResponses", "Socket data: " + data2.length);
        const chunks = this.chunkArray(data2, 1024);
        for (const chunk of chunks) {
          this.notSentChunks.push({
            data: chunk,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            conn_id: data.params.connection_id
          });
        }
      };
      socket.onError = (errorMessage) => {
        this.logger.error("handleGenericResponses", "Socket got error: ", errorMessage);
      };
      socket.onClose = (hasError) => {
        //console.log("Socket got closed", hasError);
        const pp = this.generateParams() as any;
        pp.connection_id = data.params.connection_id;
        pp.reason = "Error";
        this.send("NetworkDisconnected", pp);
      };
      this.networkConnection[data.params.connection_id].socket = socket;
      socket.open(
        data.params.host,
        data.params.port,
        () => {
          this.logger.debug("handleGenericResponses", "Socket connected");
          this.networkConnection[data.params.connection_id].socket_status = "CONNECTED";
          this.sendResponseTo("NetworkConnect", data.id, this.generateParams());
        },
        (errorMessage) => {
          this.logger.error("handleGenericResponses", "Error message: ", errorMessage, data.params);
        });
    } else if (data.method === "Data") {
      if (this.networkConnection[data.params.id]) {
        const binaryString = window.atob(data.params.data);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        this.networkConnection[data.params.id].socket.write(bytes, () => {
          this.logger.trace("handleGenericResponses", "Write succeeded");
        }, (error) => {
          this.logger.error("handleGenericResponses", "Write did not succeed: ", error);
          this.networkConnection[data.params.id].socket.close().then();
        });
      }
    } else if (data.method === "StatusEvent") {
      this.logger.info("handleGenericResponses", "Got status event ", data);
      if (data.params && data.params.ready_for_transaction) {
        this.status = "READY";
        this.status$.next(this.status);
      }
    } else if (data.method === "PosMessage") {
      this.posMessage$.next(data.params.message);
      if (this.alert) {
        this.alert.dismiss();
      }
      const alertCrtJson = {
        title: "Payment terminal",
        message: data.params.message,
        enableBackdropDismiss: true,
        buttons: []
      };
      if (data.params.message === "Varmennetaan" ||
        data.params.message === "Maksettu. Poista kortti.") {
        //do not allow to cancel those messages
        alertCrtJson.enableBackdropDismiss = false;
      } else {
        alertCrtJson.buttons.push(
          {
            text: "Ok",
            handler: data2 => {
              this.logger.trace("Cancel clicked");
            }
          }
        );
      }
      //this.alert = this.alertCtrl.create(alertCrtJson);
      //this.alert.present();
    } else if (data.response_to === "Print") {
      this.logger.debug("handleGenericResponses", "Calling print callback");
      if (this.printCallback) {
        this.printCallback();
        this.printCallback = null;
      }
    } else if (data.response_to === "Status") {
      this.logger.debug("handleGenericResponses", "Got status ", data);
      if (data.result && data.result.ready_for_transaction) {
        this.status = "READY";
        this.status$.next(this.status);
      } else {
        this.status = "NETWORK_CONNECTED";
        this.status$.next(this.status);
        setTimeout(() => {
          this.send("Status", this.generateParams());
        }, 15000);
      }
    } else if (data.response_to === "Refund") {
      //this.events.publish("printLog", "POPLATEK<-- " + JSON.stringify(data));
      if (data.error) {
        this.showPaymentError(data, true);
        return;
      }
      if (this.refundCallbackFn) {
        this.logger.debug("handleGenericResponses", "Processing refund");
        if (data.result.amount > 0) {
          this.refundCallbackFn({
            cardTerminalTransactionId: data.result.transaction_id,
            authorization: data.result.transaction_unique_id,
            cardTerminalUsageMode: data.result.card_name,
            rawRefundData: data.result
          }, null, data.result.amount);
          this.refundCallbackFn = null;
          if (this.alert) {
            this.alert.dismiss();
          }
        } else {
          this.logger.error("handleGenericResponses", "Error in refund terminal");
          this.refundCallbackFn("", "Error in terminal handling", 0);
          this.refundCallbackFn = null;
        }
      }
    } else if (data.response_to === "Purchase") {
      //this.events.publish("printLog", "POPLATEK<-- " + JSON.stringify(data));
      if (data.error) {
        this.showPaymentError(data);
        return;
      }
      if (this.payCallbackFn) {
        if (data.result.amount > 0) {
          this.payCallbackFn({
            cardTerminalTransactionId: data.result.transaction_id,
            authorization: data.result.transaction_unique_id,
            cardTerminalUsageMode: data.result.card_name,
            rawPtData: data.result
          }, null, data.result.amount);
          this.payCallbackFn = null;
          if (this.alert) {
            this.alert.dismiss();
          }
        } else {
          this.logger.error("handleGenericResponses", "Error in payment terminal");
          this.payCallbackFn("", "Error in terminal handling", 0);
          this.payCallbackFn = null;
        }
      } else {
        this.logger.error("handleGenericResponses", "Found purchase response but no callback: ", data);
      }
    } else if (data.method === "NetworkDisconnect") {
      if (data.params.connection_id) {
        if (this.networkConnection[data.params.connection_id] && this.networkConnection[data.params.connection_id].socket) {
          this.networkConnection[data.params.connection_id].socket.close();
          this.networkConnection[data.params.connection_id] = null;
          this.logger.debug("handleGenericResponses", "Closed connection: " + data.params.connection_id);
          // eslint-disable-next-line @typescript-eslint/naming-convention
          this.sendResponseTo(data.method, data.id, {connection_id: data.params.connection_id});
        }
      }
    } else {
      this.logger.error("handleGenericResponses", "Unhandled message: " + data.method, data);
    }
  }

  private showPaymentError(data, refund?) {
    let errorStr = data.error.message;
    if (data.error.data) {
      errorStr = data.error.data.string_code;
    }
    if (this.payCallbackFn) {
      if (!refund) {
        this.payCallbackFn(null, "Payment failed: " + errorStr, 0);
      } else {
        this.payCallbackFn(null, "Refund failed: " + errorStr, 0);
      }
    }
    this.payCallbackFn = null;
  }

  private jsonStringifyAscii(val) {
    return JSON.stringify(val).replace(/[\u007f-\uffff]/g,
      (x) => "\\u" + ("0000" + x.charCodeAt(0).toString(16)).substr(-4));
  }

  private sendResponseTo(method, id, params?): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!params) {
        params = {};
      }
      this.reqIdCounter++;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const data = {jsonrpc: "2.0", response_to: method, id, result: params};
      const jsonData = this.jsonStringifyAscii(data);
      //var frame = ('00000000' + jsonData.length.toString(16)).substr(-8) + ':' + jsonData + '\n';
      this.ws.send(JSON.stringify(data));
      resolve("OK");
    });
  }

  private send(method, params?, noId?): Promise<any> {
    return new Promise((resolve, reject) => {
      this.logger.info("send", "Ready state = ", this.ws.readyState, this.ws.OPEN);
      if (this.ws == null || this.ws.readyState !== this.ws.OPEN) {
        if (this.payCallbackFn) {
          this.payCallbackFn(null, "Not connected to PT");
        }
        this.logger.error("send", "Unable to send, WS not connected yet", method);
        if (reject) {
          reject();
        }
        return;
      }
      if (!params) {
        params = {};
      }
      this.reqIdCounter++;
      const data = {jsonrpc: "2.0", method, id: this.reqIdCounter + method, params};
      if (noId) {
        delete data.id;
      }
      const jsonData = this.jsonStringifyAscii(data);
      //var frame = ('00000000' + jsonData.length.toString(16)).substr(-8) + ':' + jsonData + '\n';
      this.ws.send(JSON.stringify(data));
      resolve("OK");
    });
  }



  private printOld(outputs: any, callback?: any, fontSize?: number, eject?: boolean) {
    const params = this.generateParams() as any;
    params.image = {};
    params.image.format = "png";
    params.eject = eject;
    this.createCanvas(outputs, fontSize);
    params.image.data = this.createCanvas(outputs, fontSize);
    if (callback) {
      this.printCallback = callback;
    }
    return this.send("Print", params);
  }

  private createCanvas(outputs, fontSize = 24) {
    const canvas = document.createElement("canvas");
    const height = (outputs.length) * 24 + 2 * 24;
    const width = 385;
    canvas.height = height;
    canvas.width = width;
    const context = canvas.getContext("2d");
    let i = fontSize;
    context.font = "normal normal " + fontSize + "px dejavu_sans_monobook";
    context.fillStyle = "white";
    context.fillRect(0, 0, width, height);
    context.strokeStyle = "black";
    context.fillStyle = "black";
    for (const o of outputs) {
      context.fillText(o, 0, i);
      i = i + fontSize;
    }
    let value = canvas.toDataURL("image/png");
    value = value.substring(value.indexOf("base64,") + 7);
    return value;
  }


  private returnOfGoods(amount: any, callbackFn: any, receiptNumber: any, origTransactionId: any) {
    const params = this.generateParams() as any;
    params.amount = Math.round(amount * 100);
    params.currency = "EUR";
    params.receipt_id = receiptNumber;
    if (origTransactionId) {
      params.transaction_id = origTransactionId;
    }
    if (receiptNumber) {
      params.receipt_id = receiptNumber;
    }
    this.refundCallbackFn = callbackFn;
    //this.events.publish("printLog", "POPLATEK.refund " + JSON.stringify(params));
    return this.send("Refund", params);
  }

  private getLastError() {
    return this.lastError;
  }


}
